export interface CardinalPoints {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface Coordinate {
  longitude: number;
  latitude: number;
}

export interface CenterAndZoom {
  center: Coordinate;
  zoom: number;
}

const isLatitudeValid = (latitude: number): boolean => {
  return latitude >= -90 && latitude <= 90;
}

const isLongitudeValid = (longitude: number): boolean => {
  return longitude >= -180 && longitude <= 180;
}

const isCoordinateValid = (coordinate: Coordinate): boolean => {
  return (
    typeof coordinate === "object" &&
    typeof coordinate.longitude === "number" &&
    typeof coordinate.latitude === "number" &&
    coordinate.longitude >= -180 && coordinate.longitude <= 180 &&
    coordinate.latitude >= -90 && coordinate.latitude <= 90
  );
}

export const areCoordinatesValid = (coordinates: Coordinate[]): boolean => {
  return coordinates.every(isCoordinateValid);
};

export const getBoundingCardinalPoints = (coordinates: Coordinate[]): CardinalPoints => {
  return coordinates.reduce(
    (result, item) => {
      return {
        west: Math.min(result.west, item.longitude),
        south: Math.min(result.south, item.latitude),
        east: Math.max(result.east, item.longitude),
        north: Math.max(result.north, item.latitude),
      };
    },
    {
      west: Infinity,
      south: Infinity,
      east: -Infinity,
      north: -Infinity,
    }
  );
};

export const addPaddingToBoundingCardinalPoints = (
  bounds: CardinalPoints,
  paddingRatio = Infinity
): CardinalPoints | CenterAndZoom => {
  const width = bounds.east - bounds.west;
  const height = bounds.north - bounds.south;

  const isBoundingBoxTooSmall = width < 0.0000001 || height < 0.0000001;

  if (isBoundingBoxTooSmall) {
    return {
      center: getCenter(bounds),
      zoom: 18,
    };
  }

  const newWest = bounds.west - width / paddingRatio;
  const newSouth = bounds.south - height / paddingRatio;
  const newEast = bounds.east + width / paddingRatio;
  const newNorth = bounds.north + height / paddingRatio;

  if (
    isLatitudeValid(newSouth) &&
    isLatitudeValid(newNorth) &&
    isLongitudeValid(newWest) &&
    isLongitudeValid(newEast)
  ) {
    bounds.west = newWest;
    bounds.south = newSouth;
    bounds.east = newEast;
    bounds.north = newNorth;
  }

  return bounds;
};

export const getCenter = (bounds: CardinalPoints): Coordinate => {
  let center: Coordinate = {
    longitude: bounds.west,
    latitude: bounds.south,
  };

  const width = bounds.east - bounds.west;
  const height = bounds.north - bounds.south;

  if (width !== 0) {
    center.longitude = bounds.west + width / 2;
  }

  if (height !== 0) {
    center.latitude = bounds.south + height / 2;
  }

  return center;
};

export const instanceOfCardinalPoints = (object: any): object is CardinalPoints => {
  if (!object) return false;

  return "west" in object && "east" in object && "south" in object && "north" in object;
};

export const instanceOfCenterAndZoom = (object: any): object is CenterAndZoom => {
  if (!object) return false;

  return "center" in object && "zoom" in object;
};

const haversine = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const earthRadius = 6371; 

  const lat1Rad = (lat1 * Math.PI) / 180;
  const lon1Rad = (lon1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;
  const lon2Rad = (lon2 * Math.PI) / 180;

  const dLat = lat2Rad - lat1Rad;
  const dLon = lon2Rad - lon1Rad;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = earthRadius * c;
  return distance;
}

export const isBoundingBoxLargerThan = (bounds: CardinalPoints, km: number): boolean => {
  const longitudeDistance = haversine(
    bounds.south,
    bounds.west,
    bounds.south,
    bounds.east
  );
  
  const latitudeDistance = haversine(
    bounds.south,
    bounds.west,
    bounds.north,
    bounds.west
  );

  if (longitudeDistance > km || latitudeDistance > km) {
    return true;
  }

  return false;
}